import {Component, EventEmitter, Input, NgZone, OnInit, Output} from '@angular/core';
import {AsyncPipe, NgClass, NgIf, NgOptimizedImage, TitleCasePipe} from "@angular/common";
import {Router, RouterLink, RouterLinkActive} from "@angular/router";
import {OidcSecurityService} from "angular-auth-oidc-client";
import {InlineSVGModule} from "ng-inline-svg-2";
import {TooltipModule} from "primeng/tooltip";
import {AuthService} from "../../../../auth/auth.service";
import {BehaviorSubject, Observable} from "rxjs";
import {User} from "../../../../shared/models/user.model";
import {AvatarUser} from "../../../../shared/components/avatar-component/models/avatar-user.model";
import {
  AvatarSingularComponent
} from "../../../../shared/components/avatar-component/avatar-singular/avatar-singular.component";
import {UtilsService} from "../../../../shared/services/global/utils-service.service";
import {UserRole} from "../../../../shared/enums/user-role.enum";
import {NgxZendeskWebwidgetService} from "ngx-zendesk-webwidget";
import {DialogService} from "primeng/dynamicdialog";
import {ChangelogModalComponent} from "../../../../shared/modals/changelog/changelog-modal.component";
import {NewBadgeService} from "../../../../shared/services/new-badge.service";
import {OverlayPanelModule} from "primeng/overlaypanel";
import {environment} from "../../../../../environments/environment";

@Component({
  selector: 'crm-sidebar',
  standalone: true,
  imports: [
    NgOptimizedImage,
    RouterLink,
    RouterLinkActive,
    AsyncPipe,
    NgClass,
    NgIf,
    InlineSVGModule,
    TooltipModule,
    AvatarSingularComponent,
    TitleCasePipe,
    OverlayPanelModule
  ],
  providers: [
    DialogService
  ],
  templateUrl: './sidebar.component.html',
  styleUrl: './sidebar.component.scss'
})
export class SidebarComponent implements OnInit {
  @Input() isOpen = true;
  @Output() toggle = new EventEmitter<void>();

  currentUser$: Observable<User>;
  isZendeskOpen = false;
  new_badge_items = [
    {id: 'changelog', isNew: true}
  ];
  unreadMessages = new BehaviorSubject<number>(0);
  protected readonly UserRole = UserRole;

  constructor(private readonly oidcSecurityService: OidcSecurityService,
              private readonly authService: AuthService,
              private readonly router: Router,
              public readonly utilsService: UtilsService,
              public readonly _NgxZendeskWebwidgetService: NgxZendeskWebwidgetService,
              public readonly dialogService: DialogService,
              private readonly newBadgeService: NewBadgeService,
              private readonly ngZone: NgZone
  ) {
    this.currentUser$ = authService.currentUser;
    // The Zendesk messenger runs continuous async work (rAF animation loops,
    // polling timers, a websocket and postMessage traffic). Initializing it and
    // registering its handlers inside Angular's zone makes every one of those
    // ticks trigger a full change-detection cycle, which saturates the main
    // thread and freezes the whole app when the messenger is open. Run the
    // widget outside Angular and only re-enter the zone to update bound state.
    this.ngZone.runOutsideAngular(() => {
      this._NgxZendeskWebwidgetService.initZendesk().then(() => {

        this._NgxZendeskWebwidgetService.zE("messenger", "loginUser", (callback: any) => {
          authService.getZendeskAccessTokenFromApi().subscribe({
            next: token => callback(token),
            error: err => console.error('An error occurred :', err)
          })
        });

        this._NgxZendeskWebwidgetService.zE('messenger:on', 'close', () => {
          this._NgxZendeskWebwidgetService.zE('messenger', 'hide');
          this.ngZone.run(() => this.isZendeskOpen = false);
        });

        this._NgxZendeskWebwidgetService.zE('messenger:on', 'unreadMessages', (count: any) => {
          this.ngZone.run(() => this.unreadMessages.next(count));
        });
      });
    });
  }

  ngOnInit() {
    // Initialize button states
    this.new_badge_items.forEach((button) => {
      button.isNew = !this.newBadgeService.getButtonState(button.id);
    });
  }

  // This function is used to toggle a sublist of links in the sidebar.
  toggleSublist(sublist: HTMLElement, arrowIcon: HTMLElement) {
    sublist.classList.toggle('hideSubList');
    arrowIcon.classList.toggle('-rotate-180');
  }

  getCurrentUserAvatar(): AvatarUser | undefined {
    // currentUser is null until /users/me resolves (and stays null if that call
    // fails). The collapsed-sidebar avatar renders this without a currentUser$
    // guard, so return undefined instead of dereferencing null - the avatar
    // component falls back to a placeholder and picks up the real user once it
    // loads.
    const currentUser = this.authService.currentUser.value;
    if (!currentUser?.person) return undefined;
    return new AvatarUser(currentUser.person);
  }

  logout() {
    this.oidcSecurityService.logoffAndRevokeTokens().subscribe(() => {
      this.ngZone.runOutsideAngular(() =>
        this._NgxZendeskWebwidgetService.zE("messenger", "logoutUser"));
      location.reload();
    });
  }

  onSupportButtonClick() {
    // Drive the widget outside Angular's zone so the animation/polling work it
    // schedules on open does not get patched into Angular change detection.
    this.ngZone.runOutsideAngular(() => {
      if (this.isZendeskOpen) {
        this._NgxZendeskWebwidgetService.zE('messenger', 'hide');
        this.isZendeskOpen = false;
        return;
      } else {
        this._NgxZendeskWebwidgetService.zE('messenger', 'show');
        this._NgxZendeskWebwidgetService.zE('messenger', 'open');
        this.isZendeskOpen = true;
      }
    });
  }

  // Function to check if the "new" badge should be displayed for a specific button
  shouldDisplayNewBadge(buttonId: string): boolean {
    return !this.newBadgeService.getButtonState(buttonId);
  }

  openChangelogFeature() {
    // Update the button state in local storage
    this.newBadgeService.setButtonState('changelog', true);

    this.dialogService.open(ChangelogModalComponent, {
      width: '700px',
      height: '90vh',
      closable: false,
      focusOnShow: false,
      dismissableMask: true
    });
  }

  isContactsActive(): boolean {
    return this.router.url.includes('/contacts');
  }

  protected readonly environment = environment;
}
